/* eslint-disable import/prefer-default-export */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { walletCurrenciesTypes } from '../../screens/Accounts/constants';
import { WALLETS_ALL } from '../../screens/Accounts/queries';
import {
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_REGION_SETTINGS_QUERY_KEY,
  RATES_QUERY_KEY,
} from '../sharedQueries';
import { useLedgerFilteredCurrencies, useLedgerFilteredWallets } from './ledgerWallets';
import { useSetting } from './settings';
import useRiskTierSettings from './useRiskTierSettings';
import useIsConnectFinancialCustodyUser from './custodyUser';

export const useAccountRestriction = (coin = '') => {
  const [wallets, setWallets] = useState([]);

  const { riskAssessment } = useRiskTierSettings();
  const isConnectFinancialCustodyUser = useIsConnectFinancialCustodyUser();
  const { data: userRegionSettings, isLoading: userRegionSettingsLoading } = useQuery(
    USER_REGION_SETTINGS_QUERY_KEY
  );
  const [allowBankWithdraw] = useSetting('app_features', 'feature_allow_bank_withdrawal_request');
  const [allowExchangeSetting] = useSetting('app_features', 'feature_allow_exchange');
  const { data: rates, isLoading: ratesLoading } = useQuery(RATES_QUERY_KEY);

  const [feauterPgwAllowed] = useSetting('app_features', 'feature_pgw_allowed');

  const { refetch: refetchAllWallets, isLoading: walletLoading } = useLedgerFilteredWallets(
    WALLETS_ALL,
    {
      enabled: false,
      onSettled(data, error) {
        if (!error) {
          setWallets(data);
        }
      },
    }
  );

  useEffect(() => {
    refetchAllWallets();
  }, []);

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const result = useMemo(() => {
    const restrictions = {
      receiveDisabled: true,
      sendDisabled: true,
      exchangeDisabled: true,
    };

    if (wallets && supportedCurrencies && rates) {
      wallets.forEach((wallet) => {
        let availableOnExchange = false;
        wallets.forEach((w) => {
          if (rates[`${wallet.coin.toUpperCase()}${w?.coin.toUpperCase()}`]?.availableOnExchange) {
            availableOnExchange = true;
          }
        });
        const isStableCoin = supportedCurrencies[wallet?.coin]?.fiat_currency_peg;
        const isCryptoOrStable =
          wallet?.wallet_type?.toLowerCase() === walletCurrenciesTypes.CRYPTO || isStableCoin;

        // receive restrictions
        const riskAssessmentReceiveToggle =
          riskAssessment.settings && isCryptoOrStable
            ? riskAssessment.userRisk.receive_crypto
            : true;
        const mainReceiveToggle = supportedCurrencies[wallet?.coin]?.allows_receive;

        const isAllowBuyPGW = supportedCurrencies[wallet?.coin]
          ? supportedCurrencies[wallet?.coin].allows_buy_pgw
          : false;

        const receiveDisabled =
          !(riskAssessmentReceiveToggle && mainReceiveToggle) ||
          ((!isAllowBuyPGW || !feauterPgwAllowed) &&
            !userRegionSettings?.can_fund_accounts_with_bank_transfer &&
            !isCryptoOrStable);

        // send restrictions
        const regionSettingsSendToggle =
          userRegionSettings && isCryptoOrStable
            ? userRegionSettings?.can_send_externally && userRegionSettings?.can_send_internally
            : true;

        const mainSendToggle = isCryptoOrStable
          ? supportedCurrencies[wallet?.coin]?.allows_send
          : true;

        const withdrawalTaggle = !isCryptoOrStable
          ? allowBankWithdraw &&
            supportedCurrencies[wallet?.coin]?.allows_bank_withdrawal &&
            userRegionSettings?.can_request_bank_withdrawal
          : true;

        const sendDisabled = !(regionSettingsSendToggle && mainSendToggle && withdrawalTaggle);

        // exchange restriction
        const riskAssessmentExchangeToggle = riskAssessment.settings
          ? riskAssessment.userRisk.exchange
          : true;

        const regionSettingsExchangeToggle = userRegionSettings
          ? userRegionSettings?.can_exchange
          : true;

        const exchangeDisabled = !(
          allowExchangeSetting &&
          riskAssessmentExchangeToggle &&
          regionSettingsExchangeToggle &&
          availableOnExchange
        );

        restrictions[wallet?.coin] = {
          receiveDisabled,
          sendDisabled: sendDisabled || isConnectFinancialCustodyUser,
          exchangeDisabled: exchangeDisabled || isConnectFinancialCustodyUser,
        };
      });
    }
    return restrictions;
  }, [
    supportedCurrencies,
    wallets,
    userRegionSettings,
    riskAssessment,
    allowBankWithdraw,
    allowExchangeSetting,
    rates,
    isConnectFinancialCustodyUser,
  ]);

  if (coin) {
    return result[coin]
      ? result[coin]
      : {
          receiveDisabled: true,
          sendDisabled: true,
          exchangeDisabled: true,
        };
  }

  return {
    restrictions: result,
    isLoading: walletLoading || ratesLoading || userRegionSettingsLoading,
  };
};
